import type { ComponentPropsWithRef } from "react";
import type { Link } from "react-router";
import { css, styled } from "styled-components";

import { Spinner } from "common/components/Spinner";
import { useTranslate } from "common/utilities/useTranslate";

export type Variant = "filled" | "outlined" | "neutral";

export type UIButtonProps = ButtonProps &
  (
    | (ComponentPropsWithRef<"a"> & { as?: "a" })
    | (ComponentPropsWithRef<typeof Link> & { as?: typeof Link })
    | (ComponentPropsWithRef<"button"> & { as?: "button" })
  );

type ButtonProps = {
  error?: boolean;
  fullwidth?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  success?: boolean;
  variant?: Variant;
};

const StyledButton = styled.button<UIButtonProps>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 20px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  font-size: 1rem;
  height: 40px;
  justify-content: center;
  padding: 0 1.8rem;
  text-decoration: none;
  transition:
    background-color 0.3s,
    color 0.3s;
  white-space: nowrap;
  width: ${({ fullwidth }) => (fullwidth ? "100%" : "fit-content")};

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.buttonActiveBorder};
    outline: 2px solid ${({ theme }) => theme.colors.fillPrimary};
    outline-offset: 3px;
  }

  ${({ disabled, error, isLoading, success, variant = "filled" }) => css`
    ${variant === "filled" &&
    css`
      background-color: ${({ theme }) => theme.colors.buttonFilledBackground};
      color: ${({ theme }) => theme.colors.buttonFilledText};

      &:focus-visible,
      &:hover {
        color: ${({ theme }) => theme.colors.buttonFilledActiveText};
        background-color: ${({ theme }) => theme.colors.buttonFilledActiveBackground};
      }
    `}

    ${variant === "neutral" &&
    css`
      background-color: ${({ theme }) => theme.colors.buttonNeutralBackground};
      color: ${({ theme }) => theme.colors.buttonNeutralText};
      padding: 0.625rem 1.25rem;

      &:focus-visible,
      &:hover {
        background-color: ${({ theme }) => theme.colors.buttonNeutralActiveBackground};
        color: ${({ theme }) => theme.colors.buttonNeutralActiveText};
      }
    `}

    ${variant === "outlined" &&
    css`
      border: 2px solid ${({ theme }) => theme.colors.buttonOutlinedText};
      color: ${({ theme }) => theme.colors.buttonOutlinedText};

      &:focus-visible,
      &:hover {
        background-color: ${({ theme }) => theme.colors.buttonOutlinedActiveBackground};
        color: ${({ theme }) => theme.colors.buttonOutlinedActiveText};
      }
    `}

    ${error &&
    css`
      background-color: ${({ theme }) => theme.colors.red};
      color: white;
    `}

    ${success &&
    css`
      background-color: ${({ theme }) => theme.colors.green};
      color: white;

      &:focus-visible,
      &:hover {
        background-color: ${({ theme }) => theme.colors.green};
        color: white;
      }
    `}

    ${disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}

    ${isLoading &&
    css`
      cursor: wait;
      opacity: 0.6;
    `}
  `};
`;

const StyledSpinner = styled(Spinner)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.buttonOutlinedText};
  margin-left: 1rem;
`;

export const UIButton = ({ children, ...rest }: UIButtonProps) => {
  const translate = useTranslate();
  const { isLoading = false, disabled = false } = rest;

  return (
    <StyledButton {...rest} disabled={isLoading || disabled}>
      {isLoading ? (
        <>
          {translate("app.loading")}
          <StyledSpinner />
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
};
